import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { ProposalFieldDef, ProposalFieldSchema } from '@/lib/consoleApi';
import type { RangeEntry } from '@/lib/proposalValidation';

// Schema-driven editable "proposed value" widget. Renders a list of entries
// (one per sex/scope for a reference range); each field comes from the target's
// field_schema so a future proposable target reuses this unchanged (design D-2).

function blankEntry(schema: ProposalFieldSchema): RangeEntry {
  const e: RangeEntry = {};
  for (const f of schema.entry_fields) {
    if (f.default !== undefined) e[f.key] = f.default as never;
    else if (f.type === 'enum' && f.options?.length) e[f.key] = f.options[0] as never;
  }
  return e;
}

export function seedEntries(schema: ProposalFieldSchema, current: RangeEntry[]): RangeEntry[] {
  // Start from the locked current entries when present (an edit), else one blank
  // entry (the anti-TPO "no range yet" case).
  if (current && current.length) return current.map((c) => ({ ...c }));
  return [blankEntry(schema)];
}

// Native <select> styling to match the Input primitive (the console's
// EditableConsole uses native selects too).
const CONTROL_CLASS =
  'flex h-10 w-full rounded-md border border-border bg-bg px-13 py-8 text-body text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus disabled:cursor-not-allowed disabled:opacity-50';

function FieldControl({ field, value, onChange, idSuffix }: {
  field: ProposalFieldDef; value: unknown; onChange: (v: unknown) => void; idSuffix: number;
}) {
  if (field.type === 'enum') {
    return (
      <div className="flex gap-4" role="group" aria-label={field.label}>
        {(field.options ?? []).map((opt) => (
          <Button
            key={opt}
            type="button"
            size="sm"
            variant={value === opt ? 'default' : 'outline'}
            onClick={() => onChange(opt)}
          >
            {opt}
          </Button>
        ))}
      </div>
    );
  }
  if (field.type === 'select') {
    return (
      <select
        aria-label={field.label}
        className={CONTROL_CLASS}
        value={value == null ? '' : String(value)}
        onChange={(ev) => onChange(ev.target.value === '' ? undefined : ev.target.value)}
      >
        {!field.required ? <option value="">—</option> : null}
        {(field.options ?? []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  }
  if (field.type === 'combobox') {
    // Native combobox: known values as suggestions + free entry (a new assay
    // unit like kIU/L isn't in the list yet).
    const listId = `dl-${field.key}-${idSuffix}`;
    return (
      <>
        <Input
          list={listId}
          aria-label={field.label}
          value={value == null ? '' : String(value)}
          onChange={(ev) => onChange(ev.target.value === '' ? undefined : ev.target.value)}
          placeholder="pick or type"
        />
        <datalist id={listId}>
          {(field.options ?? []).map((opt) => <option key={opt} value={opt} />)}
        </datalist>
      </>
    );
  }
  if (field.type === 'number') {
    return (
      <Input
        type="number"
        step="any"
        aria-label={field.label}
        value={value == null || value === '' ? '' : String(value)}
        onChange={(ev) => {
          const raw = ev.target.value;
          onChange(raw === '' ? undefined : Number(raw));
        }}
      />
    );
  }
  return (
    <Input
      type="text"
      aria-label={field.label}
      value={value == null ? '' : String(value)}
      onChange={(ev) => onChange(ev.target.value === '' ? undefined : ev.target.value)}
    />
  );
}

export function ProposalFields({ schema, value, onChange }: {
  schema: ProposalFieldSchema; value: RangeEntry[]; onChange: (v: RangeEntry[]) => void;
}) {
  const setEntry = (idx: number, key: string, v: unknown) => {
    const next = value.map((e, i) => (i === idx ? { ...e, [key]: v } : e));
    onChange(next);
  };
  const removeEntry = (idx: number) => onChange(value.filter((_, i) => i !== idx));
  const addEntry = () => onChange([...value, blankEntry(schema)]);

  return (
    <div className="flex flex-col gap-13">
      {value.map((entry, idx) => (
        <div key={idx} className="rounded-md border border-border bg-surface p-13">
          <div className="mb-8 flex items-center justify-between">
            <span className="text-body-sm font-medium text-text-secondary">Entry {idx + 1}</span>
            {value.length > 1 ? (
              <Button type="button" size="sm" variant="ghost" onClick={() => removeEntry(idx)}>Remove</Button>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-8 md:grid-cols-3">
            {schema.entry_fields.map((f) => (
              <label key={f.key} className="flex flex-col gap-4">
                <span className="text-[11px] text-text-muted">
                  {f.label}{f.required ? ' *' : ''}
                </span>
                <FieldControl field={f} value={entry[f.key]} onChange={(v) => setEntry(idx, f.key, v)} idSuffix={idx} />
              </label>
            ))}
          </div>
        </div>
      ))}
      <div>
        <Button type="button" size="sm" variant="outline" onClick={addEntry}>+ Add entry (e.g. sex-specific)</Button>
      </div>
    </div>
  );
}
